import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChangelogEntry } from '@/Domain/api/Changelog/dto/ChangelogEntry.dto';
import { isAbsoluteHttpUrl } from '@/Domain/api/Changelog/dto/ChangelogEntry.guard';
import { Button } from '@/Domain/components/Shared/Button/Button';

const RELEASES_PAGE_URL = 'https://quickchat.ai/releases';
const RELEASES_ASSET_BASE = 'https://quickchat.ai';
const MAX_ENTRIES = 10;

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const openExternal = (href: string): void => {
  window.open(href, '_blank', 'noopener,noreferrer');
};

const resolveImageSrc = (src: string): string =>
  isAbsoluteHttpUrl(src) ? src : `${RELEASES_ASSET_BASE}${src}`;

interface Props {
  entries: ChangelogEntry[];
  isLoading: boolean;
  isError: boolean;
}

export const ChangelogPopover = ({ entries, isLoading, isError }: Props) => {
  const { t } = useTranslation('topbar');
  const visibleEntries = entries.slice(0, MAX_ENTRIES);

  return (
    <div className="flex max-h-[70vh] flex-col" data-testid="topbar-changelog-popover">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{t('changelog.popover_heading')}</h3>
        <Button
          variant="ghost"
          size="xs"
          className="h-7 gap-1 rounded-lg px-2 text-xs text-muted-foreground"
          onClick={() => openExternal(RELEASES_PAGE_URL)}
          data-testid="topbar-changelog-view-all"
        >
          {t('changelog.view_all')}
          <ArrowUpRight className="size-3" />
        </Button>
      </div>
      <div className="overflow-y-auto px-2 py-2">
        {isLoading && (
          <p className="px-3 py-4 text-sm text-muted-foreground">{t('changelog.loading')}</p>
        )}
        {/* React Query keeps cached data when a refresh fails, so entries win over the error line. */}
        {!isLoading && isError && visibleEntries.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground">{t('changelog.error')}</p>
        )}
        {!isLoading && !isError && visibleEntries.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground">{t('changelog.empty')}</p>
        )}
        {!isLoading && visibleEntries.length > 0 && (
          <ul className="flex flex-col gap-1">
            {visibleEntries.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => openExternal(entry.url)}
                  className="group flex w-full cursor-pointer flex-col gap-2 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent"
                  data-testid={`topbar-changelog-entry-${entry.id}`}
                >
                  <span className="text-xs text-muted-foreground">
                    {dateFormatter.format(new Date(entry.date))}
                  </span>
                  <span className="text-sm font-medium text-foreground">{entry.title}</span>
                  {entry.image && (
                    <img
                      src={resolveImageSrc(entry.image.src)}
                      alt={entry.image.alt}
                      loading="lazy"
                      className="w-full rounded-md border border-border"
                    />
                  )}
                  <span className="line-clamp-3 text-xs text-muted-foreground">
                    {entry.description}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
